import { AngleTool } from "iconoir-react";
import { useTranslation } from "react-i18next";
import {
  EmptyState,
  PageHeader,
  SimpleLoader,
  ViewSubSection,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { useWidgetSetup } from "./useWidgetSetup";
import { Combobox } from "@ttc3k/trekker";

type WidgetSetupProps = {
  onPreviewWidgetClick: () => void;
};

export const WidgetSetup = ({ onPreviewWidgetClick }: WidgetSetupProps) => {
  const { t } = useTranslation();

  const {
    allTagGroupsAndItems,
    filterableTags,
    loadingUpdateMutation,
    operatorsHaveNoFilters,
    selectedTagIds,
    handleUpdateSelectedTagIds,
    onTagInputValueSearch,
  } = useWidgetSetup();

  return (
    <Flex flexDir={"column"} gap={"400"}>
      <PageHeader
        title={`${t("widgets:PAGE.WIDGET")} ${t("widgets:DETAILS.TABS.SETUP")}`}
        description={t("widgets:DETAILS.TABS.DESCRIPTIONS.SETUP")}
        Icon={AngleTool}
        ctaButtonCopy={t("widgets:DETAILS.PAGE_ACTIONS.PREVIEW_WIDGET")}
        ctaButtonProps={{
          onClick: onPreviewWidgetClick,
        }}
      />
      {/* FILTERS */}
      <ViewSubSection
        title={t("widgets:DETAILS.TABS.VIEW_SUBSECTIONS.SETUP.FILTERS.TITLE")}
        description={t(
          "widgets:DETAILS.TABS.VIEW_SUBSECTIONS.SETUP.FILTERS.DESCRIPTION",
        )}
      >
        {allTagGroupsAndItems?.length > 0 ? (
          <Combobox.Root
            value={selectedTagIds}
            onValueChange={(d) => handleUpdateSelectedTagIds(d.value)}
            onInputValueChange={(d) => onTagInputValueSearch(d.inputValue)}
            // Note that the top-level group items need to be excluded from this array
            items={allTagGroupsAndItems.flatMap((tagGroup) => tagGroup.tags)}
            placeholder={`${t("widgets:DETAILS.TABS.VIEW_SUBSECTIONS.SETUP.FILTERS.PLACEHOLDER_LABEL")}...`}
            css={{
              "& [data-scope='combobox'][data-part='input'], [data-scope='combobox'][data-part='input-wrapper']":
                {
                  bg: "white",
                },
            }}
            multiple
            tagVisualVariant="accent.blue"
            tagModeVariant="light"
            disabled={loadingUpdateMutation}
          >
            {filterableTags.length > 0 &&
              filterableTags.map((tagGroup) => (
                <Combobox.ItemGroup key={tagGroup.key}>
                  <Combobox.ItemGroupLabel>
                    {tagGroup.label}
                  </Combobox.ItemGroupLabel>
                  {tagGroup.tags.map((tag) => (
                    <Combobox.Item
                      paddingLeft={"300"}
                      key={tag.value}
                      item={tag}
                      label={tag.label}
                    />
                  ))}
                </Combobox.ItemGroup>
              ))}
          </Combobox.Root>
        ) : operatorsHaveNoFilters ? (
          <EmptyState
            text={t(
              "widgets:DETAILS.TABS.VIEW_SUBSECTIONS.SETUP.FILTERS.EMPTY",
            )}
          />
        ) : (
          <SimpleLoader />
        )}
      </ViewSubSection>
    </Flex>
  );
};
